import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CalendarClock,
  Check,
  CheckCheck,
  Clock,
  Copy,
  Loader2,
  Search,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { ContextoKpiTile } from "@/components/shared/ContextoKpiTile";
import { Person } from "@/components/shared/person";
import { cn } from "@/lib/utils";
import { comunicadoSistemaQueries } from "@/services/api/queries/comunicado-sistema.queries";
import { ComunicadoSistemaAceitePessoaDTO } from "@/types/comunicado-sistema.type";

interface Props {
  comunicadoId: number;
  enabled?: boolean;
  /** Fim da vigência: vira o prazo do aceite (crítico sempre tem, §2.5). */
  fimEm?: string;
}

/** Quantas pessoas justificam mostrar a busca. Abaixo disso ela só ocupa espaço. */
const LIMITE_BUSCA = 8;

const fmt = (d?: string) =>
  d ? new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "";

/** Dias inteiros até o prazo. Negativo = já venceu. */
function diasAte(fimEm?: string): number | null {
  if (!fimEm) return null;
  const ms = new Date(fimEm).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

function rotuloPrazo(dias: number | null): { valor: string; tom: string; iconTom: string } {
  if (dias === null) {
    return {
      valor: "Sem prazo",
      tom: "border-border bg-muted/40 text-foreground",
      iconTom: "bg-muted text-muted-foreground",
    };
  }
  if (dias < 0) {
    return {
      valor: "Encerrado",
      tom: "border-border bg-muted/40 text-muted-foreground",
      iconTom: "bg-muted text-muted-foreground",
    };
  }
  const valor = dias === 0 ? "Hoje" : dias === 1 ? "Amanhã" : `${dias} dias`;
  // Últimos 2 dias com pendentes é o momento de cobrar — o tile fica quente.
  const urgente = dias <= 2;
  return {
    valor,
    tom: urgente
      ? "border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
      : "border-border bg-card text-foreground",
    iconTom: urgente
      ? "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300"
      : "bg-muted text-muted-foreground",
  };
}

/**
 * Painel de confirmações de leitura do comunicado crítico. Responde três
 * perguntas do admin, nessa ordem: **quanto falta**, **quanto tempo resta** e
 * **quem** exatamente falta (para poder cobrar) — daí os KPIs no topo, o prazo
 * ao lado e o botão de copiar os nomes dos pendentes.
 */
export function ComunicadoAceitesPanel({ comunicadoId, enabled = true, fimEm }: Props) {
  const { data, isLoading } = useQuery({
    ...comunicadoSistemaQueries.pendentes(comunicadoId),
    enabled,
  });

  const [busca, setBusca] = useState("");

  const filtrar = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return (pessoas: ComunicadoSistemaAceitePessoaDTO[]) =>
      termo ? pessoas.filter((p) => p.nome.toLowerCase().includes(termo)) : pessoas;
  }, [busca]);

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const pct = data.total > 0 ? Math.round((data.aceitosCount / data.total) * 100) : 0;
  const completo = data.pendentesCount === 0 && data.total > 0;
  const prazo = rotuloPrazo(diasAte(fimEm));

  const copiarPendentes = () => {
    navigator.clipboard.writeText(data.pendentes.map((p) => p.nome).join(", "));
    toast.success("Nomes dos pendentes copiados.");
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <ContextoKpiTile
          icon={<Users className="h-5 w-5" />}
          label="Público-alvo"
          value={String(data.total)}
          tone="border-border bg-card text-foreground"
          iconTone="bg-muted text-muted-foreground"
        />
        <ContextoKpiTile
          icon={<CheckCheck className="h-5 w-5" />}
          label="Confirmaram"
          value={`${data.aceitosCount} de ${data.total}`}
          tone="border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200"
          iconTone="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300"
        />
        <ContextoKpiTile
          icon={<CalendarClock className="h-5 w-5" />}
          label="Prazo para confirmar"
          value={prazo.valor}
          tone={prazo.tom}
          iconTone={prazo.iconTom}
        />
      </div>

      <div>
        <div className="mb-1.5 flex items-center justify-between text-sm">
          <span className="font-medium text-foreground">
            {completo
              ? "Todo o público-alvo confirmou a leitura"
              : `${data.pendentesCount} ainda não confirmaram`}
          </span>
          <span className="font-semibold tabular-nums text-muted-foreground">{pct}%</span>
        </div>
        <Progress
          value={pct}
          className="bg-muted"
          indicatorClassName={completo ? "bg-emerald-500" : "bg-amber-500"}
        />
      </div>

      {data.total > LIMITE_BUSCA && (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar pessoa nas duas listas..."
            className="pl-9"
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ListaPessoas
          titulo="Pendentes"
          tom="text-amber-600 dark:text-amber-400"
          icone={<Clock className="h-3.5 w-3.5" />}
          pessoas={filtrar(data.pendentes)}
          total={data.pendentesCount}
          vazio={data.total === 0 ? "Ninguém no público-alvo." : "Todos já confirmaram 🎉"}
          acao={
            data.pendentesCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={copiarPendentes}
              >
                <Copy className="h-3.5 w-3.5" /> Copiar nomes
              </Button>
            )
          }
        />
        <ListaPessoas
          titulo="Confirmaram"
          tom="text-emerald-600 dark:text-emerald-400"
          icone={<Check className="h-3.5 w-3.5" />}
          // Mais recentes primeiro: o que o admin acompanha é o que acabou de entrar.
          pessoas={filtrar(
            [...data.aceitos].sort((a, b) => (b.aceitoEm ?? "").localeCompare(a.aceitoEm ?? "")),
          )}
          total={data.aceitosCount}
          vazio="Ninguém confirmou ainda."
        />
      </div>
    </div>
  );
}

function ListaPessoas({
  titulo,
  tom,
  icone,
  pessoas,
  total,
  vazio,
  acao,
}: {
  titulo: string;
  tom: string;
  icone: React.ReactNode;
  pessoas: ComunicadoSistemaAceitePessoaDTO[];
  total: number;
  vazio: string;
  acao?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col rounded-xl border border-border/60 bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
        <p
          className={cn(
            "flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide",
            tom,
          )}
        >
          {icone}
          {titulo}
          <span className="rounded-full bg-muted px-1.5 text-[11px] font-medium text-foreground/70">
            {total}
          </span>
        </p>
        {acao}
      </div>

      {total === 0 ? (
        <p className="px-3 py-8 text-center text-xs text-muted-foreground">{vazio}</p>
      ) : pessoas.length === 0 ? (
        <p className="px-3 py-8 text-center text-xs text-muted-foreground">
          Ninguém com esse nome nesta lista.
        </p>
      ) : (
        <ul className="custom-scrollbar max-h-72 divide-y divide-border/40 overflow-y-auto">
          {pessoas.map((p) => (
            <li key={p.id} className="flex items-center gap-2 px-3 py-2">
              {/* `asClickable`: abre o perfil — daqui o admin vai cobrar a pessoa. */}
              <Person.Root pessoa={p} asClickable className="min-w-0 flex-1">
                <Person.Avatar />
                <Person.Name className="text-sm" />
                <Person.Status />
              </Person.Root>
              {p.aceitoEm && (
                <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                  {fmt(p.aceitoEm)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
